/**
 * Inhalt des Profilmenüs (REQ-20 §5) — Port von
 * `Components/Tooltips/IwmTooltipProfil.razor` im neuen Popup-Stil.
 *
 * Inhalt und Verhalten bleiben funktional gleich; neu ist der Dichte-Umschalter
 * aus dem Entwurf (Zeilen 254–258). Die Beschriftung „Logout" ist wörtlich die
 * des Altclients (REQ-00 §4).
 */

import type { ReactElement } from 'react';

import { useAuth } from '@/auth/useAuth';
import { APP_VERSION } from '@/config/appConfig';
import { useDensity, type Density } from '@/hooks/useDensity';
import type { App } from '@/types/entities';

import { ProfileAvatar } from './ProfileAvatar';

const DENSITIES: { value: Density; label: string; title: string }[] = [
  { value: 'compact', label: 'Kompakt', title: 'Kompakt' },
  { value: 'normal', label: 'Standard', title: 'Standard' },
  { value: 'cozy', label: 'Weit', title: 'Komfortabel' },
];

export function ProfileMenu(): ReactElement {
  const auth = useAuth();
  const { density, setDensity } = useDensity();

  /** Pendant zu `SwitchApp`: Ein Klick auf die aktive App tut nichts. */
  const switchApp = (app: App): void => {
    if (app.URI === auth.currentApp?.URI) {
      return;
    }
    void auth.selectApp(app);
  };

  return (
    <>
      <div className="iwm-profile-menu-head">
        <ProfileAvatar image={auth.profileImage} name={auth.user?.Name ?? null} size={44} />
        <div className="iwm-profile-menu-identity">
          <div className="iwm-profile-menu-name">{auth.user?.Name}</div>
          <div className="iwm-profile-menu-mail">{auth.user?.EMail}</div>
          <div className="iwm-profile-menu-app">{auth.currentApp?.Name}</div>
        </div>
      </div>

      <div className="iwm-profile-menu-section">
        <div className="iwm-profile-menu-caption">Dichte</div>
        <div className="iwm-density-toggle" role="group" aria-label="Dichte">
          {DENSITIES.map((entry) => (
            <button
              key={entry.value}
              type="button"
              title={entry.title}
              aria-pressed={density === entry.value}
              onClick={() => {
                setDensity(entry.value);
              }}
            >
              {entry.label}
            </button>
          ))}
        </div>
      </div>

      <div className="iwm-profile-menu-section iwm-profile-app-menu">
        <div className="iwm-profile-menu-caption">Anwendung</div>
        {auth.apps?.map((app) => {
          const active = app.URI === auth.currentApp?.URI;
          return (
            <button
              key={app.URI}
              type="button"
              className={`iwm-profile-app-menu-item${active ? ' active' : ''}`}
              aria-current={active ? 'true' : undefined}
              onClick={() => {
                switchApp(app);
              }}
            >
              {app.Name}
            </button>
          );
        })}
      </div>

      <div className="iwm-profile-menu-section">
        <button
          type="button"
          className="iwm-profile-menu-logout"
          onClick={() => {
            void auth.logout();
          }}
        >
          Logout
        </button>
      </div>

      <div className="iwm-profile-menu-footer">
        <a href="https://iwm.cloud/" target="_blank" rel="noreferrer">
          © IWM IT Solutions
        </a>
        <span className="iwm-num">v{APP_VERSION}</span>
      </div>
    </>
  );
}

export default ProfileMenu;
